var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useState } from 'react';
import { Box, Button, Checkbox, IconButton, InputBase, MenuItem, Select, Stack, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import { baseColors } from '../../../Themes/colors';
import { DOCS_BACK_CUSTOM_CATEGORIES } from './DocsBackReasonsForm';
import { DocumentTypeTag } from './ReasonsNoticePreview';
var CUSTOM_REASON_MAX_LENGTH = 1000;
var ACCENT = baseColors.bluePreset[10];
export var normalizeReasonText = function (text) { return text.trim().replace(/\s+/g, ' ').toLowerCase(); };
var DOCUMENT_TYPE_HINTS = [
    { pattern: /invoice|sales order/, type: 'Invoice/Sales Order' },
    { pattern: /driver'?s? licen[cs]e|government issued id|\bid\b/, type: "Driver's License" },
    { pattern: /voided check|void check/, type: 'Voided Checks' },
    { pattern: /bank statement/, type: 'Bank Statements' },
    { pattern: /tax return/, type: 'Tax Returns' },
    { pattern: /tax exempt/, type: 'Tax Exemption Certificates' },
    { pattern: /financial statement/, type: 'Financial Statements' },
    { pattern: /\bsign(ed)?\b|signature/, type: 'Signed Documents' },
];
var suggestDocumentType = function (text) {
    var _a, _b;
    var value = text.toLowerCase();
    return (_b = (_a = DOCUMENT_TYPE_HINTS.find(function (hint) { return hint.pattern.test(value); })) === null || _a === void 0 ? void 0 : _a.type) !== null && _b !== void 0 ? _b : '';
};
var DUPLICATE_MESSAGES = {
    unchangedCopy: "You haven't changed the copied text yet. Edit it to save it as a new reason.",
    custom: 'This reason is already in your past reasons. Tick it there instead, or change the text.',
    predefined: 'This reason is already a predefined reason. Select it from Predefined reasons instead, or change the text.',
    new: "You've already added this reason. Change the text to add a different one.",
};
var fieldSx = { fontSize: 14, borderRadius: '8px', backgroundColor: baseColors.fullWhite, border: "1px solid ".concat(baseColors.greyPreset[20]) };
var searchFieldSx = __assign(__assign({}, fieldSx), { borderRadius: '4px', minHeight: 44, pl: 1.25, pr: 0.75, '&:hover': { borderColor: baseColors.successPreset[10] }, '&:focus-within': { borderColor: baseColors.successPreset[30] } });
var searchInputSx = { fontSize: 14, color: baseColors.darkPreset[10], '& input::placeholder': { color: baseColors.greyPreset[5], opacity: 1 } };
var ROW_ACTIVE_BG = baseColors.bluePreset[60];
var CustomReasonsPanel = function (_a) {
    var pastReasons = _a.pastReasons, selectedIds = _a.selectedIds, onTogglePast = _a.onTogglePast, newReasons = _a.newReasons, onAddNew = _a.onAddNew, onRemoveNew = _a.onRemoveNew, findExisting = _a.findExisting, _b = _a.isLoading, isLoading = _b === void 0 ? false : _b;
    var _c = useState(''), query = _c[0], setQuery = _c[1];
    var _d = useState(true), isListOpen = _d[0], setIsListOpen = _d[1];
    var _e = useState(0), activeIndex = _e[0], setActiveIndex = _e[1];
    var _f = useState(false), isComposing = _f[0], setIsComposing = _f[1];
    var _g = useState(''), draftText = _g[0], setDraftText = _g[1];
    var _h = useState(''), draftType = _h[0], setDraftType = _h[1];
    var _j = useState(false), isTypeTouched = _j[0], setIsTypeTouched = _j[1];
    var _k = useState(''), copiedFrom = _k[0], setCopiedFrom = _k[1];
    var _l = useState(false), showDuplicate = _l[0], setShowDuplicate = _l[1];
    var trimmedQuery = normalizeReasonText(query);
    var rows = (function () {
        var matches = function (text, type) { return !trimmedQuery || normalizeReasonText(text).includes(trimmedQuery) || normalizeReasonText(type !== null && type !== void 0 ? type : '').includes(trimmedQuery); };
        var added = __spreadArray([], newReasons, true).reverse()
            .filter(function (reason) { return matches(reason.text, reason.category); })
            .map(function (reason) { return ({ key: reason.key, text: reason.text, documentType: reason.category, checked: true, toggle: function () { return onRemoveNew(reason.key); } }); });
        var past = pastReasons
            .filter(function (reason) { return matches(reason.label, reason.documentType); })
            .map(function (reason) { return ({ key: reason.id, text: reason.label, documentType: reason.documentType, checked: selectedIds.includes(reason.id), toggle: function () { return onTogglePast(reason.id); } }); });
        return __spreadArray(__spreadArray([], added, true), past, true);
    })();
    var highlighted = Math.min(activeIndex, rows.length - 1);
    var pickRow = function (row) {
        row.toggle();
        setQuery('');
        setActiveIndex(0);
    };
    var openForm = function (text, type, fromText) {
        if (fromText === void 0) { fromText = ''; }
        setDraftText(text);
        setDraftType(type);
        setIsTypeTouched(Boolean(fromText));
        setCopiedFrom(fromText);
        setIsComposing(true);
        setIsListOpen(false);
    };
    var startNew = function () { return openForm(query.trim(), suggestDocumentType(query)); };
    var closeForm = function () {
        setIsComposing(false);
        setDraftText('');
        setDraftType('');
        setIsTypeTouched(false);
        setCopiedFrom('');
        setShowDuplicate(false);
        setIsListOpen(true);
    };
    var trimmedDraft = draftText.trim();
    var duplicateOf = trimmedDraft ? findExisting(trimmedDraft) : undefined;
    var isSuggested = !isTypeTouched && Boolean(draftType);
    var canAdd = Boolean(trimmedDraft && draftType);
    var isUnchangedCopy = Boolean(copiedFrom) && normalizeReasonText(trimmedDraft) === normalizeReasonText(copiedFrom);
    var handleAdd = function () {
        if (!canAdd)
            return;
        if (duplicateOf) {
            setShowDuplicate(true);
            return;
        }
        onAddNew(trimmedDraft, draftType);
        setQuery('');
        setActiveIndex(0);
        closeForm();
    };
    var handleSearchKeyDown = function (event) {
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setIsListOpen(true);
            setActiveIndex(function (index) { return Math.min(rows.length - 1, index + 1); });
        }
        else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActiveIndex(function (index) { return Math.max(0, index - 1); });
        }
        else if (event.key === 'Enter') {
            event.preventDefault();
            if (isListOpen && rows.length > 0)
                pickRow(rows[highlighted]);
            else if (query.trim())
                startNew();
        }
        else if (event.key === 'Escape') {
            setIsListOpen(false);
        }
    };
    return (React.createElement(Stack, { gap: 1.5 },
        React.createElement(Box, null,
            React.createElement(Stack, { direction: "row", alignItems: "center", sx: searchFieldSx },
                React.createElement(Box, { component: "span", className: "icon-Research", sx: { fontSize: 13, color: baseColors.greyPreset[10], mr: 1 } }),
                React.createElement(InputBase, { fullWidth: true, placeholder: "Search past reasons", value: query, onChange: function (event) { setQuery(event.target.value); setIsListOpen(true); setActiveIndex(0); }, onFocus: function () { return setIsListOpen(true); }, onKeyDown: handleSearchKeyDown, inputProps: { 'aria-label': 'Search past reasons', autoComplete: 'off' }, sx: searchInputSx }),
                React.createElement(IconButton, { size: "small", "aria-label": "Show or hide past reasons", onClick: function () { return setIsListOpen(function (open) { return !open; }); }, sx: { p: 0.5 } },
                    React.createElement(Box, { component: "span", className: "icon-arrow_drop_down", sx: { fontSize: 16, fontWeight: 700, color: baseColors.darkPreset[10], transform: isListOpen ? 'none' : 'rotate(180deg)', transition: 'transform 0.15s' } }))),
            isListOpen && (React.createElement(Box, { sx: { mt: 0.75, border: "1px solid ".concat(baseColors.greyPreset[40]), borderRadius: '8px', overflow: 'hidden' } }, rows.length > 0 ? (React.createElement(Box, { role: "listbox", "aria-multiselectable": true, sx: { maxHeight: 240, overflowY: 'auto' } }, rows.map(function (row, index) { return (React.createElement(Box, { key: row.key, onMouseEnter: function () { return setActiveIndex(index); }, sx: {
                    borderBottom: "1px solid ".concat(baseColors.greyPreset[55]),
                    bgcolor: index === highlighted || row.checked ? ROW_ACTIVE_BG : baseColors.fullWhite,
                } },
                React.createElement(Stack, { role: "option", "aria-selected": row.checked, direction: "row", alignItems: "flex-start", gap: 0.5, onClick: function () { return pickRow(row); }, sx: { cursor: 'pointer', px: 0.5, pt: 0.5 } },
                    React.createElement(Checkbox, { size: "small", checked: row.checked, tabIndex: -1, sx: { p: 0.5, '&.Mui-checked': { color: ACCENT } }, inputProps: { 'aria-label': row.text, readOnly: true } }),
                    React.createElement(Typography, { variant: "body2", color: baseColors.darkPreset[10], sx: { pt: 0.6, minWidth: 0, wordBreak: 'break-word' } },
                        row.text,
                        row.documentType && React.createElement(Box, { component: "span", sx: { ml: 0.75, display: 'inline-flex', verticalAlign: 'middle', lineHeight: 'normal' } },
                            React.createElement(DocumentTypeTag, { label: row.documentType })))),
                React.createElement(Button, { size: "small", startIcon: React.createElement(ContentCopyIcon, { sx: { fontSize: '12px !important' } }), onClick: function () { var _a; return openForm(row.text, (_a = row.documentType) !== null && _a !== void 0 ? _a : '', row.text); }, "aria-label": "Copy and edit ".concat(row.text), sx: { ml: 4, mb: 0.25, minHeight: 0, py: 0.25, px: 0.75, textTransform: 'none', fontSize: 13, fontWeight: 600, textDecoration: 'underline', color: baseColors.bluePreset[50] } }, "Copy & edit"))); }))) : (React.createElement(Typography, { variant: "body2", color: baseColors.greyPreset[5], sx: { p: 1.5 } }, isLoading
                ? 'Loading reasons…'
                : trimmedQuery
                    ? "No past reason matches \u201C".concat(query.trim(), "\u201D. Press Enter to add it as a new reason.")
                    : 'No past custom reasons yet.'))))),
        !isComposing ? (React.createElement(Button, { variant: "outlined", fullWidth: true, startIcon: React.createElement(AddIcon, { sx: { fontSize: '16px !important' } }), onClick: startNew, sx: { textTransform: 'none', fontWeight: 700, color: ACCENT, borderColor: ACCENT, borderRadius: '8px' } }, "Add a new reason")) : (React.createElement(Stack, { gap: 1.25, sx: { borderTop: "1px solid ".concat(baseColors.greyPreset[40]), pt: 1.5 } },
            React.createElement(Stack, { direction: "row", alignItems: "baseline", justifyContent: "space-between" },
                React.createElement(Typography, { variant: "body2", fontWeight: 800, color: baseColors.darkPreset[10] }, "New reason"),
                React.createElement(Button, { size: "small", onClick: closeForm, sx: { minWidth: 0, textTransform: 'none', fontWeight: 600, textDecoration: 'underline', color: baseColors.bluePreset[50] } }, "Cancel")),
            copiedFrom && (React.createElement(Typography, { variant: "caption", color: baseColors.greyPreset[5], sx: { mt: -0.75 } },
                "Copied from \u201C",
                copiedFrom,
                "\u201D. The original stays unchanged.")),
            React.createElement(Stack, { gap: 0.5 },
                React.createElement(Typography, { component: "label", htmlFor: "custom-reason-text", variant: "caption", fontWeight: 700, color: baseColors.darkPreset[10] }, "What should the dealer provide or confirm?"),
                React.createElement(InputBase, { id: "custom-reason-text", multiline: true, minRows: 3, value: draftText, onChange: function (event) {
                        setDraftText(event.target.value);
                        setShowDuplicate(false);
                        if (!isTypeTouched)
                            setDraftType(suggestDocumentType(event.target.value));
                    }, inputProps: { maxLength: CUSTOM_REASON_MAX_LENGTH }, sx: __assign(__assign({}, fieldSx), { px: 1.5, py: 1, '& textarea': { wordBreak: 'break-word', lineHeight: 1.4 } }) })),
            React.createElement(Stack, { gap: 0.5 },
                React.createElement(Typography, { component: "label", variant: "caption", fontWeight: 700, color: baseColors.darkPreset[10] }, "Document type"),
                React.createElement(Select, { size: "small", displayEmpty: true, fullWidth: true, value: draftType, onChange: function (event) { setDraftType(String(event.target.value)); setIsTypeTouched(true); }, inputProps: { 'aria-label': 'Document type' }, renderValue: function (selected) { return (selected ? String(selected) : React.createElement(Box, { component: "span", sx: { color: baseColors.greyPreset[30] } }, "Select a document type")); }, sx: { height: 40, fontSize: 14, borderRadius: '8px', bgcolor: baseColors.fullWhite } }, DOCS_BACK_CUSTOM_CATEGORIES.map(function (category) { return (React.createElement(MenuItem, { key: category, value: category, sx: { fontSize: 14 } }, category === 'Acknowledge' ? 'Acknowledge (no document)' : category)); })),
                isSuggested && (React.createElement(Typography, { variant: "caption", color: baseColors.greyPreset[5] }, "Suggested from your text. Change it if needed."))),
            showDuplicate && duplicateOf && (React.createElement(Typography, { variant: "caption", color: baseColors.errorPreset[20], role: "alert" }, DUPLICATE_MESSAGES[isUnchangedCopy ? 'unchangedCopy' : duplicateOf])),
            React.createElement(Button, { variant: "contained", disableElevation: true, disabled: !canAdd, onClick: handleAdd, sx: { textTransform: 'none', fontWeight: 700, borderRadius: '8px', bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT } } }, "Add reason")))));
};
export default CustomReasonsPanel;
